import { AnimatedNumber } from '@components/ui/AnimateNumber';
import { Badge } from '@components/ui/Badge';
import { Checkbox } from '@components/ui/Checkbox';
import { DottedBorderSide } from '@components/ui/DottedBorder';
import { LinkButton } from '@components/ui/LinkButton';
import { PricingBadge } from '@components/ui/PricingBadge';
import { Ul } from '@components/ui/Ul';
import { useState } from 'react';

export function PricingTiers() {
  const [price, setPrice] = useState(25);
  return (
    <div className="relative mx-auto mt-8 grid grid-cols-12 justify-center sm:px-6 lg:max-w-[76rem] xl:gap-x-8 xl:px-0">
      <div className="absolute inset-x-0 top-px">
        <DottedBorderSide side="top" color="%23131316" overhang={150} />
      </div>

      <div className="relative z-10 col-span-full col-start-1 border-l border-r border-t border-newGray-950/10 border-t-[#42434D] bg-newGray-50 p-6 lg:col-span-4 lg:border-r-0 xl:border-r xl:p-8">
        <div className="absolute inset-0 xl:hidden">
          <DottedBorderSide side="left" overhang={0} color="%23131316" />
          <DottedBorderSide side="right" overhang={0} color="%23131316" />
        </div>
        <div className="relative z-10">
          <div className="space-y-2 md:w-3/5 lg:w-full">
            <h2 className="text[0.8125rem] font-semibold leading-6 text-primary">
              Free plan
            </h2>
            <p className="text-[0.9375rem] leading-5 text-secondary">
              Everything you need to get started in minutes. For free.
            </p>
          </div>
          <div className="mb-5 mt-6 flex">
            <span className="mr-1.5 self-start text-[0.6875rem] font-medium text-primary">
              US
            </span>
            <span className="mr-2 text-[3rem] font-semibold leading-[3.5rem] text-primary">
              $0
            </span>
            <span className="self-center text-[0.8125rem] leading-6 text-tertiary">
              per month
            </span>
          </div>
          <LinkButton href="https://dashboard.clerk.com/apps/new">
            Get Started for free
          </LinkButton>
          <div className="mt-8 space-y-4">
            <h3 className="text-[0.8125rem] font-semibold text-primary">
              Start building
            </h3>
            <Ul checkMarkStyle="gray">
              <li>No credit card required</li>
              <li>10,000 monthly active users</li>
              <li>Pre-built components</li>
              <li>Custom Domain</li>
            </Ul>
          </div>
        </div>
      </div>
      <div className="relative z-10 col-span-full col-start-1 border-x border-t border-x-newGray-950/10 border-t-[#00AEE3] bg-white shadow-md md:flex lg:col-start-5 xl:gap-[1.875rem]">
        <div className="pointer-events-none absolute inset-0 hidden -translate-x-[0.5px] sm:block lg:translate-x-0">
          <DottedBorderSide side="left" overhang={0} color="%23131316" />
        </div>
        <div className="pointer-events-none absolute inset-0 hidden sm:block">
          <DottedBorderSide side="right" overhang={0} color="%23131316" />
        </div>

        <div className="relative border-newGray-950/10 p-6 md:w-1/2 lg:border-r xl:border-r-0 xl:p-8">
          <div className="pointer-events-none absolute inset-0 hidden xl:block">
            <DottedBorderSide side="right" overhang={0} color="%23131316" />
          </div>

          <div>
            <div className="space-y-2">
              <h2 className="text[0.8125rem] font-semibold leading-6 text-[#00AEE3]">
                Pro plan
              </h2>
              <p className="text-[0.9375rem] leading-5 text-secondary">
                Advanced functionality to take your app to the next level.
              </p>
            </div>
            <div className="mb-5 mt-6 flex">
              <span className="mr-1.5 self-start text-[0.6875rem] font-medium">
                US
              </span>
              <span className="mr-2 text-[3rem] font-semibold leading-[3.5rem]">
                $<AnimatedNumber value={price} />
              </span>
              <span className="self-center text-[0.8125rem] leading-6 text-tertiary">
                per month
              </span>
            </div>
            <LinkButton
              href="https://dashboard.clerk.com/last-active?path=/plan-billing"
              intent="skyBlue"
            >
              Upgrade to Pro
            </LinkButton>
            <div className="mt-8 space-y-4">
              <h3 className="text-[0.8125rem] font-semibold text-primary">
                Scale your app
              </h3>
              <Ul checkMarkStyle="blue">
                <li>$0.02 per MAU after 10,000</li>
                <li>Remove Clerk Branding</li>
                <li>Allowlist / Blocklist</li>
                <li>Customizable Session Duration</li>
                <li>...and much more</li>
              </Ul>
            </div>
          </div>
        </div>

        <div className="relative sm:flex sm:border-t sm:border-t-newGray-800/20 md:block md:w-1/2 md:border-t-0 lg:border-l-0 lg:border-l-newGray-950/10">
          <div className="pointer-events-none absolute inset-0 hidden md:block">
            <DottedBorderSide side="left" overhang={0} color="%23131316" />
          </div>
          <div className="space-y-3 border-newGray-950/5 p-6 sm:border-r md:border-b md:border-r-0 xl:p-8">
            <div className="space-y-3 md:flex md:gap-3 md:space-y-0">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enhanced-authentication"
                  onCheckedChange={checked =>
                    setPrice(prev => (checked ? prev + 100 : prev - 100))
                  }
                />
                <label
                  htmlFor="enhanced-authentication"
                  className="truncate text-[0.8125rem] font-semibold md:max-w-[156px] xl:max-w-full"
                >
                  Enhanced Authentication add-on
                </label>
              </div>
              <PricingBadge>$100</PricingBadge>
            </div>
            <Ul checkMarkStyle="blue">
              <li>Multi-factor auth (SMS, TOTP, Backup codes)</li>
              <li>Device tracking and revocation </li>
              <li>Simultaneous sessions</li>
              <li>SAML authentication</li>
            </Ul>
          </div>

          <div className="space-y-3 p-6 xl:p-8">
            <div className="space-y-3 md:flex md:gap-3 md:space-y-0">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enhanced-administration"
                  onCheckedChange={checked =>
                    setPrice(prev => (checked ? prev + 100 : prev - 100))
                  }
                />
                <label
                  htmlFor="enhanced-administration"
                  className="truncate text-[0.8125rem] font-semibold md:max-w-[156px] xl:max-w-full"
                >
                  Enhanced Administration add-on
                </label>
              </div>
              <PricingBadge>$100</PricingBadge>
            </div>
            <Ul checkMarkStyle="blue">
              <li>User impersonation</li>
              <li>Enhanced roles </li>
              <li>
                Audit logs <Badge label="Coming soon" />
              </li>
            </Ul>
          </div>
        </div>
      </div>

      <div
        className="relative col-span-full col-start-1 border-t border-black/5"
        style={{
          marginLeft: 'calc((((1440 - 1216) / 2) / 16) * -1rem)',
          marginRight: 'calc((((1440 - 1216) / 2) / 16) * -1rem)'
        }}
      />

      <div className="col-span-full col-start-1 h-4 bg-newGray-50" />

      <div className="relative isolate z-10 col-span-full col-start-1 overflow-hidden bg-newGray-950">
        <div className="pointer-events-none absolute top-0 -translate-x-[12%] -translate-y-[56%] [mask:linear-gradient(black_50%,transparent_95%)] lg:-translate-y-[50%] lg:translate-x-[40%] xl:-translate-y-[48%] xl:translate-x-[39%]">
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <Logo />
        </div>
        <div className="pointer-events-none absolute inset-y-px right-px border-l border-white/5 bg-white/[0.005] backdrop-blur-md sm:hidden md:block md:w-1/2 lg:w-1/3 xl:w-[32%]" />

        <div className="grid grid-cols-12 pt-6 md:py-6 lg:space-y-8 xl:gap-x-8 xl:py-8">
          <div className="col-span-full col-start-1 mb-8 space-y-2 self-start px-6 md:col-span-6 lg:col-span-full lg:mb-0 xl:px-8">
            <h3 className="text-[0.8125rem] font-medium leading-6 text-[#FDE047]">
              B2B SaaS Suite
            </h3>
            <p className="text-[1.25rem] font-semibold leading-7 tracking-[-0.015em] text-white">
              The easy solution to <br className="lg:hidden" />
              multi-tenancy.
            </p>
          </div>

          <div className="col-span-full col-start-1 mb-8 px-6 md:col-span-6 md:mb-8 lg:col-span-4 xl:px-8">
            <div className="space-y-4">
              <h3 className="flex gap-1.5 text-[0.8125rem] font-semibold text-white">
                B2B SaaS features in <Badge label="Free Plan" />
              </h3>

              <Ul textColor="light">
                <li>
                  Purpose-built components and APIs for managing teams and
                  organizations
                </li>
                <li>Free for 100 monthly active organizations</li>
                <li>Up to 5 members per organization</li>
                <li>Invitation flows and basic roles</li>
              </Ul>
            </div>
          </div>
          <div className="col-span-full col-start-1 mb-8 space-y-4 px-6 md:mb-0 lg:col-span-4 lg:col-start-5 xl:px-8">
            <h3 className="flex gap-1.5 text-[0.8125rem] font-semibold text-white">
              B2B SaaS features in <Badge label="Pro Plan" intent="skyBlue" />
            </h3>
            <Ul checkMarkStyle="blue" textColor="light">
              <li>$1 per MAO after 100</li>
              <li>Unlimited members per organization</li>
            </Ul>
          </div>

          <div className="relative col-span-full col-start-1 px-6 py-8 md:col-start-7 md:row-start-1 md:-translate-y-[0.4375rem] md:pb-0 md:pt-0 lg:col-span-4 lg:col-start-9 lg:row-start-2 xl:px-8 ">
            <div className="pointer-events-none absolute inset-px -z-10 border-t border-white/5 bg-newGray-950 sm:bg-white/[0.025] sm:backdrop-blur-md md:hidden" />
            <div className="mb-4 gap-2 space-y-3 md:flex md:space-y-0 md:pb-0 xl:justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="enhanced-b2b-saas"
                  intent="secondary"
                  onCheckedChange={checked =>
                    setPrice(prev => (checked ? prev + 100 : prev - 100))
                  }
                />
                <label
                  htmlFor="enhanced-b2b-saas"
                  className="truncate text-[0.8125rem] font-semibold text-white md:max-w-[156px] xl:max-w-full"
                >
                  Enhanced B2B SaaS add-on
                </label>
              </div>
              <PricingBadge intent="secondary">$100</PricingBadge>
            </div>
            <Ul checkMarkStyle="yellow" textColor="light">
              <li>Domain restrictions</li>
              <li>Automatic invitations </li>
              <li>
                Custom roles and permissions{' '}
                <Badge intent="yellow" label="Coming soon" />
              </li>
            </Ul>
          </div>
        </div>
      </div>

      <div className="border-x-1 bg col-span-full col-start-1 h-4 bg-newGray-50" />
    </div>
  );
}

function Logo() {
  return (
    <svg
      width="844"
      height="1066"
      viewBox="0 0 844 1066"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      fillOpacity={0.05}
      stroke="white"
      strokeWidth={1}
      strokeOpacity={0.1}
    >
      <path
        d="M700.525 533C700.525 624.99 625.849 699.563 533.733 699.563C441.617 699.563 366.941 624.99 366.941 533C366.941 441.01 441.617 366.438 533.733 366.438C625.849 366.438 700.525 441.01 700.525 533Z"
        fill="white"
      />
      <path
        d="M830.385 89.8438C847.059 100.997 848.484 124.459 834.294 138.63L712.404 260.352C701.387 271.353 684.297 273.091 670.432 265.997C629.431 245.022 582.966 233.187 533.733 233.187C367.923 233.187 233.508 367.418 233.508 533C233.508 582.165 245.359 628.567 266.363 669.511C273.467 683.358 271.726 700.424 260.71 711.425L138.82 833.148C124.63 847.319 101.136 845.896 89.9674 829.245C33.1424 744.525 0 642.628 0 533C0 238.632 238.96 0 533.733 0C643.511 0 745.549 33.0969 830.385 89.8438Z"
        fill="white"
      />
      <path
        d="M834.286 927.37C848.477 941.541 847.052 965.002 830.378 976.155C745.542 1032.9 643.504 1066 533.725 1066C423.947 1066 321.909 1032.9 237.072 976.156C220.398 965.003 218.974 941.541 233.164 927.37L355.054 805.647C366.07 794.646 383.16 792.908 397.026 800.002C438.027 820.978 484.492 832.813 533.725 832.813C582.959 832.813 629.424 820.978 670.425 800.002C684.29 792.908 701.38 794.646 712.397 805.647L834.286 927.37Z"
        fill="white"
      />
    </svg>
  );
}
